
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Calendar, TrendingUp, CheckCircle2, XCircle, Clock, DollarSign, Activity } from 'lucide-react';
import { Rental } from '@/services/types';

interface RentalStatsCardProps {
  rentals: Rental[];
  totalCount: number;
  totalRevenue?: number; // Новый пропс для выручки из платежей
  loading?: boolean;
}

interface AnimatedCounterProps {
  value: number;
  duration?: number;
  format?: (value: number) => string;
}

const AnimatedCounter: React.FC<AnimatedCounterProps> = ({ 
  value, 
  duration = 1000, 
  format = (v) => v.toString() 
}) => {
  const [displayValue, setDisplayValue] = React.useState(0);

  React.useEffect(() => {
    let startTime: number;
    let animationFrame: number;

    const animate = (currentTime: number) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setDisplayValue(Math.floor(easeOutQuart * value));

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [value, duration]);

  return <span>{format(displayValue)}</span>;
};

export const RentalStatsCard: React.FC<RentalStatsCardProps> = ({ 
  rentals, 
  totalCount, 
  totalRevenue = 0, // Используем переданную выручку из платежей
  loading = false 
}) => {
  const stats = React.useMemo(() => {
    // Убираем расчет выручки из аренд, используем переданную из платежей
    const activeRentals = rentals.filter(r => r.status === 'active').length;
    const completedRentals = rentals.filter(r => r.status === 'completed').length;
    const cancelledRentals = rentals.filter(r => r.status === 'cancelled').length;
    const pendingRentals = rentals.filter(r => r.status === 'pending').length;
    
    const totalMileage = rentals.reduce((sum, rental) => sum + (rental.total_mileage || 0), 0);
    const avgRentalValue = totalRevenue > 0 && totalCount > 0 ? totalRevenue / totalCount : 0;
    const completionRate = rentals.length > 0 ? (completedRentals / rentals.length) * 100 : 0;

    return {
      totalRevenue, // Используем переданную выручку
      activeRentals,
      completedRentals,
      cancelledRentals,
      pendingRentals,
      totalMileage,
      avgRentalValue,
      completionRate
    };
  }, [rentals, totalRevenue, totalCount]);

  const formatCurrency = (amount: number) => 
    new Intl.NumberFormat('ru-RU', { 
      style: 'currency', 
      currency: 'RUB',
      minimumFractionDigits: 0
    }).format(amount);

  const formatNumber = (num: number) => 
    new Intl.NumberFormat('ru-RU').format(num);

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Статистика аренд
          </CardTitle>
          <CardDescription>Загрузка статистики...</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="space-y-2">
                <div className="h-4 bg-muted animate-pulse rounded" />
                <div className="h-6 bg-muted animate-pulse rounded" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calendar className="h-5 w-5" />
          Статистика аренд
        </CardTitle>
        <CardDescription>
          Общая информация по {formatNumber(totalCount)} арендам
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {/* Общая выручка из успешных платежей */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <DollarSign className="h-4 w-4" />
              Общая выручка
            </div>
            <div className="text-2xl font-bold text-green-600">
              <AnimatedCounter 
                value={stats.totalRevenue} 
                format={formatCurrency}
                duration={1500}
              />
            </div>
            <div className="text-xs text-muted-foreground">
              из успешных платежей
            </div>
          </div>

          {/* Активные аренды */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Activity className="h-4 w-4" />
              Активные
            </div>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold text-blue-600">
                <AnimatedCounter value={stats.activeRentals} />
              </span>
              {stats.pendingRentals > 0 && (
                <Badge variant="secondary">
                  +<AnimatedCounter value={stats.pendingRentals} /> ожид.
                </Badge>
              )}
            </div>
          </div>

          {/* Завершенные аренды */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <CheckCircle2 className="h-4 w-4" />
              Завершено
            </div>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold text-green-600">
                <AnimatedCounter value={stats.completedRentals} />
              </span>
              <Badge variant="outline" className="text-green-600 border-green-200">
                <AnimatedCounter 
                  value={stats.completionRate} 
                  format={(v) => `${v.toFixed(1)}%`}
                />
              </Badge>
            </div>
          </div>

          {/* Средний чек */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <TrendingUp className="h-4 w-4" />
              Средний чек
            </div>
            <div className="text-2xl font-bold text-purple-600">
              <AnimatedCounter 
                value={stats.avgRentalValue} 
                format={formatCurrency}
                duration={1200}
              />
            </div>
            {stats.cancelledRentals > 0 && (
              <div className="flex items-center gap-1 text-red-600 text-xs">
                <XCircle className="h-3 w-3" />
                <AnimatedCounter value={stats.cancelledRentals} /> отменено
              </div>
            )}
          </div>
        </div>

        {/* Дополнительная статистика */}
        {stats.totalMileage > 0 && (
          <div className="mt-4 pt-4 border-t">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Общий пробег:</span>
              <span className="font-medium">
                <AnimatedCounter 
                  value={stats.totalMileage} 
                  format={(v) => `${formatNumber(v)} км`}
                />
              </span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
